import { useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { Calendar } from '../../Common';

interface IDatePickerSimpleProps {
    label: string;
    value: DateTime | null;
    onChange: (date: DateTime) => void;
    minDateIso?: string;
    maxDateIso?: string;
    enablePastDays?: boolean;
    isError?: boolean;
    errorMessage?: string;
}

const DatePickerSimple = ({
    label,
    value,
    onChange,
    minDateIso,
    maxDateIso,
    enablePastDays = false,
    isError,
    errorMessage,
}: IDatePickerSimpleProps) => {
    const [displayDate, setDisplayDate] = useState(value ?? DateTime.now());
    const [showCalendar, setShowCalendar] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setShowCalendar(false);
            }
        };
        if (showCalendar) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showCalendar]);

    const handleMonthChange = (_: DateTime, action?: string) => {
        if (action === 'right') setDisplayDate(prev => prev.plus({ months: 1 }));
        if (action === 'left') setDisplayDate(prev => prev.minus({ months: 1 }));
    };

    const handleSelect = (date: DateTime) => {
        onChange(date);
        setShowCalendar(false);
    };

    const formattedValue = value ? value.toFormat('MMM dd, yyyy') : '';

    return (
        <div className="change-project-status__date-picker" ref={wrapperRef}>
            <label className="change-project-status__end-date-label">{label}</label>
            <div
                className={`change-project-status__date-trigger${isError ? ' change-project-status__date-trigger--error' : ''}`}
                onClick={() => setShowCalendar(prev => !prev)}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && setShowCalendar(prev => !prev)}
            >
                <span className={formattedValue ? '' : 'change-project-status__date-trigger-placeholder'}>{formattedValue || 'Select date'}</span>
                <img src="/svg/calendar-gray.svg" alt="" />
            </div>
            {isError && errorMessage && <span className="change-project-status__end-date-error">{errorMessage}</span>}
            {showCalendar && (
                <div className="change-project-status__calendar-wrapper">
                    <Calendar
                        displayDate={displayDate}
                        singleDate={value ?? undefined}
                        onClick={handleSelect}
                        changeMonth={handleMonthChange}
                        enablePastDays={enablePastDays}
                        disabledWeekends={false}
                        activeArrows={{ active: true, firstCalendarLeftButton: true, secondCalendarRightButton: true }}
                        minUtcDateString={minDateIso}
                        maxUtcDateString={maxDateIso}
                    />
                </div>
            )}
        </div>
    );
};

export default DatePickerSimple;
